import { useCallback } from "react";
import {
  createUserSkill as postUserSkill,
  deleteUserSkill,
  detachAgentSkill,
  detachDatasetSkill,
  getSkill as fetchSkill,
  invalidateAllSkillLists,
  type PartialSkill,
  type Skill,
  type SkillFields,
  updateSkill as patchSkill,
} from "~/services/skills";

export interface SkillMutations {
  createUserSkill: (fields: SkillFields) => Promise<void>;
  updateSkill: (skillId: string, fields: Partial<SkillFields>) => Promise<void>;
  deleteSkill: (skill: PartialSkill) => Promise<void>;
  getSkill: (skillId: string) => Promise<Skill>;
}

// Skill create/update/delete for the conversation composer. Mirrors the legacy
// assistant's handlers: each write revalidates every skills cache so the
// picker reflects the change immediately. `deleteSkill` dispatches by scope —
// USER skills are deleted, AGENT/DATASET skills are detached from their owner.
export function useSkillMutations(): SkillMutations {
  const createUserSkill = useCallback(async (fields: SkillFields) => {
    await postUserSkill(fields);
    await invalidateAllSkillLists();
  }, []);

  const updateSkill = useCallback(async (skillId: string, fields: Partial<SkillFields>) => {
    await patchSkill(skillId, fields);
    await invalidateAllSkillLists();
  }, []);

  const deleteSkill = useCallback(async (skill: PartialSkill) => {
    switch (skill.scope) {
      case "USER":
        await deleteUserSkill(skill.id);
        break;
      case "AGENT":
        if (!skill.scope_id) {
          throw new Error(`AGENT-scope skill ${skill.id} is missing scope_id`);
        }
        await detachAgentSkill(skill.scope_id, skill.id);
        break;
      case "DATASET":
        if (!skill.scope_id) {
          throw new Error(`DATASET-scope skill ${skill.id} is missing scope_id`);
        }
        await detachDatasetSkill(skill.scope_id, skill.id);
        break;
      default:
        throw new Error(`Cannot delete ${skill.scope}-scope skill from this surface`);
    }
    await invalidateAllSkillLists();
  }, []);

  const getSkill = useCallback(async (skillId: string): Promise<Skill> => fetchSkill(skillId), []);

  return { createUserSkill, updateSkill, deleteSkill, getSkill };
}
